import CreateText from 'common/CreateText';
import GridContainer from 'common/GridContainer';
import TableStructure from 'common/TableStructure';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormatTime } from 'common/formatters';

import { useEffect, useState } from 'react';
import AppointmentBadge, { getAppointmentDisplayStatus } from '../AppointmentBadge';

const ContainerDiv = styled.div`
    background: ${(props) => props.theme.colorBackground};
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flexgrow: 1;

    .button {
        padding: 0px 60px;
    }

    .text {
        margin: 22px 0px;
    }
`;

const DateTimeContainer = styled.div`
    display: flex;
    flex-direction: column;
    line-height: 1.4;
    padding: 0;
    margin: 0;
    
    .time {
        display: block;
        line-height: 1.4;
        margin: 0;
        padding: 0;
    }
    .date {
        display: block;
        font-family: Poppins;
        font-weight: 400;
        font-size: 12px;
        color: rgba(153, 153, 153, 1);
        margin-top: 2px;
        line-height: 1.4;
        padding: 0;
    }
`;

export default function DayView({ date, rows, togglePopup }) {
    const createDateString = (date) => {
        return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate().toString().padStart(2, '0');
    };

    const columns = [
        {
            field: 'scheduled',
            headerName: <FormattedMessage id="be.seen.appointment.table.scheduled" defaultMessage="Scheduled" />,
            width: 120,
            flex: 1,
            renderCell: (params) => {
                // Format date as "Day AbbreviatedMonth" (e.g., "25 Dec" or "1 Jan") without leading zeros
                let formattedDate = '';
                if (params.value || params.row?.appointment_date) {
                    const dateValue = params.value || params.row?.appointment_date;
                    const dateMoment = moment(dateValue);
                    if (dateMoment.isValid()) {
                        // Format: D = day without leading zero, MMM = abbreviated month
                        formattedDate = dateMoment.format('D MMM');
                    } else {
                        formattedDate = dateValue || '';
                    }
                }
                return (
                    <DateTimeContainer>
                        {params.row.time && <span className="time">{FormatTime(params.row.time)}</span>}
                        <span className="date">{formattedDate}</span>
                    </DateTimeContainer>
                );
            },
        },
        {
            field: 'patient',
            headerName: <FormattedMessage id="be.seen.appointment.table.patient.name" defaultMessage="Patient" />,
            width: 150,
            flex: 1,
            valueGetter: (params) => `${params?.first_name || 'Patient'}`
        },
        {
            field: 'type',
            headerName: <FormattedMessage id="be.seen.appointment.table.service" defaultMessage="Service" />,
            width: 150,
            flex: 1,
            renderCell: (params) => {
                if (params.row.service_data && params.row.service_data.length > 0) {
                    // Show the first service name
                    const serviceName = params.row.service_data[0].serviceName || params.row.service_data[0].description;
                    
                    // If there are multiple services, show the count
                    if (params.row.service_data.length > 1) {
                        return `${serviceName} +${params.row.service_data.length - 1}`;
                    }
                    
                    return serviceName;
                }
                
                return "N/A";
            },
        },
        {
            field: 'provider_name',
            headerName: (
                <FormattedMessage id="be.seen.appointment.table.provider.name" defaultMessage="Provider" />
            ),
            width: 150,
            flex: 1,
        },
        {
            field: 'status',
            headerName: <FormattedMessage id="be.seen.appointment.table.status" defaultMessage="Status" />,
            width: 120,
            flex: 1,
            renderCell: (params) => {
                // Use shared logic so 'Past' resolves consistently (handles formats/timezones),
                // but keep 'pending' as 'pending' instead of showing 'in review' in Day view.
                const displayStatus = getAppointmentDisplayStatus(
                    params.value,
                    params.row?.appointment_date,
                    params.row?.time,
                );

                let badgeStatus;
                if ((displayStatus || '').toLowerCase() === 'past') {
                    // Explicitly show "Past" when helper marks it as such
                    badgeStatus = 'Past';
                } else if (params.value) {
                    // For non-past statuses, use the raw status from the database
                    // so 'pending' stays 'pending' to match Week/Month views.
                    badgeStatus = params.value;
                } else {
                    // Fallback to helper result or 'pending'
                    badgeStatus = displayStatus || 'pending';
                }

                return <AppointmentBadge appointmentStatus={badgeStatus} />;
            },
        },
        {
            field: '',
            headerName: '',
            sortable: false,
            flex: 0.4,
            minWidth: 70,
            renderCell: (params) => {
                return (
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{
                            cursor: 'pointer',
                            margin: 'auto',
                        }}
                    >
                        <path
                            d="M9 18L15 12L9 6"
                            stroke="#9ca3af"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                );
            },
        },
    ];

    const [filteredRows, setFilteredRows] = useState([]);

    useEffect(() => {
        const formattedDate = moment(date).format('DD-MM-YYYY');

        let processedRows = rows.filter((row) => {
            return moment(row?.appointment_date).format('DD-MM-YYYY') === formattedDate;
        });

        setFilteredRows(processedRows);
    }, [date, rows]);
    
    return (
        <>
            {filteredRows?.length > 0 ? (
                <GridContainer>
                    <TableStructure
                        rows={filteredRows}
                        columns={columns}
                        handleRowClick={(e) => {
                            togglePopup(
                                e,
                                rows.find((row) => row.id === e.row.id),
                            );
                        }}
                    ></TableStructure>
                </GridContainer>
            ) : (
                <div style={{ position: 'relative' }}>
                    <div className="empty">
                        <TableStructure
                            rows={[]}
                            columns={columns}
                            handleRowClick={(e) => {
                                togglePopup(
                                    e,
                                    rows.find((row) => row.id === e.row.id),
                                );
                            }}
                        />
                    </div>
                    <div style={{
                        position: 'absolute',
                        top: 'calc(40px + 35%)',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        fontFamily: 'Poppins, sans-serif',
                        pointerEvents: 'none'
                    }}>
                        No appointments on this day
                    </div>
                </div>
            )}
        </>
    );
}
